import { recordDataRoomHeartbeats } from '@workspace/api-backend'
import { useCallback, useEffect, useRef } from 'react'

const HEARTBEAT_INTERVAL_MS = 5_000
const FLUSH_INTERVAL_MS = 10_000
const IDLE_TIMEOUT_MS = 60_000

interface HeartbeatEntry {
  section: string
  resourceId: string | null
  timestamp: string
}

function flushWithKeepalive(token: string, sessionId: string, heartbeats: HeartbeatEntry[]) {
  const url = `${import.meta.env.VITE_BACKEND_URL}/api/v1/shares/${token}/heartbeats`
  fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    keepalive: true,
    body: JSON.stringify({ sessionId, heartbeats }),
  }).catch(() => {})
}

function getOrCreateSessionId(token: string): string {
  const key = `dr-session-${token}`
  const existing = sessionStorage.getItem(key)
  if (existing) return existing
  const id = crypto.randomUUID()
  sessionStorage.setItem(key, id)
  return id
}

export function useDataRoomTracker(token: string) {
  const sessionId = useRef(getOrCreateSessionId(token))
  const currentSection = useRef('page')
  const currentResourceId = useRef<string | null>(null)
  const buffer = useRef<HeartbeatEntry[]>([])
  const heartbeatTimer = useRef<ReturnType<typeof setInterval> | null>(null)
  const flushTimer = useRef<ReturnType<typeof setInterval> | null>(null)
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isIdle = useRef(false)

  const flush = useCallback(
    (keepalive = false) => {
      if (buffer.current.length === 0) return

      const heartbeats = [...buffer.current]
      buffer.current = []

      if (keepalive) {
        flushWithKeepalive(token, sessionId.current, heartbeats)
        return
      }

      recordDataRoomHeartbeats({
        path: { token },
        body: {
          sessionId: sessionId.current,
          heartbeats,
        },
      }).catch(() => {})
    },
    [token]
  )

  const pushHeartbeat = useCallback(() => {
    buffer.current.push({
      section: currentSection.current,
      resourceId: currentResourceId.current,
      timestamp: new Date().toISOString(),
    })
  }, [])

  const startTracking = useCallback(() => {
    if (heartbeatTimer.current || document.hidden) return
    heartbeatTimer.current = setInterval(pushHeartbeat, HEARTBEAT_INTERVAL_MS)
    flushTimer.current = setInterval(() => flush(), FLUSH_INTERVAL_MS)
  }, [pushHeartbeat, flush])

  const stopTracking = useCallback(() => {
    if (heartbeatTimer.current) {
      clearInterval(heartbeatTimer.current)
      heartbeatTimer.current = null
    }
    if (flushTimer.current) {
      clearInterval(flushTimer.current)
      flushTimer.current = null
    }
  }, [])

  const goIdle = useCallback(() => {
    if (isIdle.current) return
    isIdle.current = true
    stopTracking()
    flush()
  }, [stopTracking, flush])

  const resetIdleTimer = useCallback(() => {
    if (document.hidden) return

    if (idleTimer.current) {
      clearTimeout(idleTimer.current)
    }
    idleTimer.current = setTimeout(goIdle, IDLE_TIMEOUT_MS)

    if (isIdle.current) {
      isIdle.current = false
      pushHeartbeat()
      startTracking()
    }
  }, [goIdle, pushHeartbeat, startTracking])

  const setSection = useCallback((section: string, resourceId?: string) => {
    currentSection.current = section
    currentResourceId.current = resourceId ?? null
  }, [])

  useEffect(() => {
    if (!document.hidden) {
      pushHeartbeat()
      startTracking()
      resetIdleTimer()
    }

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopTracking()
        flush()
        if (idleTimer.current) {
          clearTimeout(idleTimer.current)
          idleTimer.current = null
        }
      } else {
        isIdle.current = false
        pushHeartbeat()
        startTracking()
        resetIdleTimer()
      }
    }

    const handleBeforeUnload = () => {
      stopTracking()
      flush(true)
    }

    const activityEvents = ['mousemove', 'mousedown', 'scroll', 'keydown', 'touchstart'] as const
    for (const event of activityEvents) {
      document.addEventListener(event, resetIdleTimer, { passive: true })
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      stopTracking()
      flush()
      if (idleTimer.current) {
        clearTimeout(idleTimer.current)
      }
      for (const event of activityEvents) {
        document.removeEventListener(event, resetIdleTimer)
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [pushHeartbeat, startTracking, stopTracking, flush, resetIdleTimer])

  return { setSection }
}
